import { makeStyles } from '@material-ui/core/styles';
import { Handle, Position } from '@xyflow/react';
import classnames from 'classnames';
import hexToRgba from 'hex-rgba';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import { ModalType, NodeType, PlatformType } from '../../app/types';
import { changeSeconds } from '../../app/utils';
import IconLineMessage from '../../assets/images/Icons/IconLineMessage';
import icDelete20 from '../../assets/images/svg/ic-delete-20.svg';
import icFb from '../../assets/images/svg/ic-fb-messenger.svg';
import icInLine from '../../assets/images/svg/ic-in-line.svg';
import icInstagram from '../../assets/images/svg/ic-instagram.svg';
import icLineOfficialAccount from '../../assets/images/svg/ic-line-official-account.svg';
import icSavedReply from '../../assets/images/svg/ic-saved-reply.svg';
import icWhatsapp from '../../assets/images/svg/ic_WhatsApp.svg';
import icLivechat from '../../assets/images/svg/ic_livechat.svg';
import {
  checkNode,
  deleteConditionsNode,
  deleteNode,
  setIsShowSettingBar,
  setSelectedNode,
} from '../../features/mainSlice';
import { openModal } from '../../features/modalSlice';
import Tooltip from '../shared/Tooltip';

const useStyles = makeStyles(
  {
    root: {
      width: 240,
      borderRadius: '12px',
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      border: 0,
    },
    selected: {
      border: `solid 1px ${color.$1}`,
    },
    error: {
      border: `solid 1px ${color.$17}`,
    },
    highlightPath: {
      border: `solid 1px ${color.$3}!important`,
    },
    handle: {
      border: '0 !important',
      borderRadius: '0 !important',
      background: `${color.$3} !important`,
      width: '1px !important',
      height: '1px !important',
      minWidth: '1px !important',
      minHeight: '1px !important',
    },
    title: {
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      fontSize: '13px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 14px 8px 16px',
      background: hexToRgba(color.$1, 6),
      '& div': {
        display: 'flex',
        alignItems: 'center',
      },
      '& > svg': {
        marginRight: 2,
      },
    },
    errorTitle: {
      color: color.$17,
      background: hexToRgba(color.$17, 6),
    },
    content: {
      padding: '12px 16px',
    },
    name: {
      fontSize: '15px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      marginBottom: 12,
      '& > img': {
        marginRight: 5,
        width: 16,
        height: 16,
      },
    },
    message: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.38',
      letterSpacing: 'normal',
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 0',
      background: color.$19,
      '& > img': {
        marginRight: 4,
      },
    },
    time: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: color.$19,
      marginBottom: 8,
      padding: '0 6px',
      '& > img': {
        marginRight: 4,
      },
      '& > div': {
        textOverflow: 'ellipsis',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: '1',
        display: '-webkit-box',
        overflow: 'hidden',
        lineHeight: '32px',
        letterSpacing: 'normal',
        color: color.$3,
        fontSize: '13px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
      },
    },
  },
  {
    name: 'Message',
  },
);

const MessageComponent = ({ id, data, isConnectable }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isErrorNodeIds = useSelector(store => store.ma.isErrorNodeIds);
  const isActive = useSelector(store => store.ma.isActive);
  const selectedNode = useSelector(store => store.ma.selectedNode);
  const nodes = useSelector(store => store.ma.data?.nodes) || [];
  const edges = useSelector(store => store.ma.data?.edges) || [];
  const allPathNodes = useSelector(store => store.ma.highlightPathNodes) || [];
  const isPreview = useSelector(store => store.ma.isPreview);
  const platform = useSelector(store => store.ma.data?.platform);
  const node = useMemo(() => nodes.find(n => n?.id === id), [nodes]);
  const [isHover, setIsHover] = useState(false);
  const sourceNodeId = useMemo(
    () => (edges.find(e => e?.target === id) || {})?.source,
    [edges],
  );
  const targetNodeId = useMemo(
    () => (edges.find(e => e?.source === id) || {})?.target,
    [edges],
  );
  const sourceNode = useMemo(
    () => nodes.find(n => n?.id === sourceNodeId),
    [sourceNodeId],
  );
  const targetNode = useMemo(
    () => nodes.find(n => n?.id === targetNodeId),
    [targetNodeId],
  );

  const isHighlightPath = isPreview && !!allPathNodes.find(n => n?.id === id);
  const isSelected = selectedNode?.id === id;
  const isError =
    !!~isErrorNodeIds.indexOf(id) &&
    !checkNode({ type: NodeType.MESSAGE, data, platform });

  const { messages, name, waitTime } = data || {};

  const waitText = useMemo(() => {
    const year = changeSeconds(waitTime, 'year');
    const month = changeSeconds(waitTime, 'month');
    const day = changeSeconds(waitTime, 'day');
    const hour = changeSeconds(waitTime, 'hour');
    const minute = changeSeconds(waitTime, 'minute');

    if (waitTime === null || (!year && !month && !day && !hour && !minute)) {
      return t('message-node.not-wait');
    }

    return t('message-node.wait', {
      text: `${year ? `${t('message-node.year', { year })}` : ''}${
        month ? `${t('message-node.month', { month })}` : ''
      }${day ? `${t('message-node.day', { day })}` : ''}${
        hour ? `${t('message-node.hour', { hour })}` : ''
      }${minute ? `${t('message-node.minute', { minute })}` : ''}`,
    });
  }, [waitTime]);

  const messageText = useMemo(() => {
    const messageLength = (messages || []).length;
    if (messageLength) {
      if ((messages || []).length > 1) {
        return t('message-node.more');
      }

      const message = (messages || [])[0] || {};
      if (message?.waTemplateId !== undefined) {
        return t(`types.message-template-type.whatsappTemplate`);
      }
      return t(
        `types.message-template-type.${
          message?.templateType ||
          message?.data?.templateType ||
          message?.contentType
        }`,
      );
    }
    return '';
  }, [messages]);

  const platformIcon = useMemo(() => {
    switch (platform) {
      case PlatformType.FACEBOOK:
        return icFb;
      case PlatformType.INSTAGRAM:
        return icInstagram;
      case PlatformType.WHATSAPP:
        return icWhatsapp;
      case PlatformType.LIVECHAT:
        return icLivechat;
      case PlatformType.LINE:
      default:
        return icLineOfficialAccount;
    }
  }, [platform]);

  function onDelete() {
    // 若底下是加入條件時，則連同加入條件下的所有node一併刪除
    if (targetNode?.type === NodeType.CONDITION) {
      dispatch(
        openModal({
          modalType: ModalType.DIALOG_TEXT,
          modalProps: {
            title: t('delete-message-conditions-modal.title'),
            content: t('delete-message-conditions-modal.content'),
            cancelText: t('delete-message-conditions-modal.cancelText'),
            submitText: t('delete-message-conditions-modal.submitText'),
          },
          onClick: async () => {
            await dispatch(deleteConditionsNode(targetNode?.data?.groupId, id));
          },
        }),
      );
      // 若上方是自動上標，且底下也是自動上標，則連同下方自動上標一並刪除
    } else if (
      sourceNode?.type === NodeType.TAGGING &&
      targetNode?.type === NodeType.TAGGING
    ) {
      dispatch(
        openModal({
          modalType: ModalType.DIALOG_TEXT,
          modalProps: {
            title: t('delete-message-tag-modal.title'),
            content: t('delete-message-tag-modal.content', {
              name: node?.data?.name,
            }),
            cancelText: t('delete-message-tag-modal.cancelText'),
            submitText: t('delete-message-tag-modal.submitText'),
          },
          onClick: async () => {
            await dispatch(deleteNode(id));
            await dispatch(deleteNode(targetNodeId));
          },
        }),
      );
    } else {
      dispatch(
        openModal({
          modalType: ModalType.DIALOG_TEXT,
          modalProps: {
            title: t('delete-node-modal.title'),
            content: t('delete-node-modal.content', { name }),
            cancelText: t('delete-node-modal.cancelText'),
            submitText: t('delete-node-modal.submitText'),
          },
          onClick: () => {
            dispatch(deleteNode(id));
          },
        }),
      );
    }
  }

  const onSelect = () => {
    if (!isSelected) {
      dispatch(setSelectedNode(node));
    }
    dispatch(setIsShowSettingBar(true));
  };

  return (
    <div>
      <Handle
        className={classes.handle}
        type="target"
        position={Position.Top}
        style={{ top: 0 }}
        isConnectable={isConnectable}
      />
      <div
        className={classnames(classes.root, {
          [classes.selected]: isSelected,
          [classes.error]: isError,
          [classes.highlightPath]: isHighlightPath,
        })}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onClick={onSelect}
      >
        <div
          className={classnames(classes.title, {
            [classes.errorTitle]: isError,
          })}
        >
          <div>
            <IconLineMessage backgroundColor={isError ? color.$17 : color.$2} />
            {t('add-message')}
          </div>
          {isHover && !isActive && (
            <img
              style={{ cursor: 'pointer' }}
              onClick={() => onDelete()}
              src={icDelete20}
              alt="delete"
            />
          )}
        </div>
        <div className={classes.content}>
          <div className={classes.name}>
            <img src={platformIcon} alt="name" />
            {name}
          </div>
          <div className={classes.time}>
            <img src={icInLine} alt="time" />
            <Tooltip placement="top" title={waitText}>
              <div>{waitText}</div>
            </Tooltip>
          </div>
          <div className={classes.message}>
            <img src={icSavedReply} alt="message" />
            {messageText}
          </div>
        </div>
      </div>
      <Handle
        className={classes.handle}
        type="source"
        position={Position.Bottom}
        style={{ bottom: 0 }}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export default MessageComponent;
